import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { localePath } from "@/i18n/utils";
import { SUPPORTED_LOCALES, LOCALE_LABELS, type Locale } from "@/i18n/translations";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { locale, t } = useLanguage();

  const isHome = location.pathname === "/" || location.pathname === `/${locale}` || location.pathname === `/${locale}/`;

  const linkClass = "text-foreground/50 hover:text-foreground transition-colors text-xs tracking-[0.2em] uppercase";

  const scrollLink = (id: string, label: string) => {
    if (isHome) {
      return (
        <a href={`#${id}`} onClick={() => setOpen(false)} className={linkClass}>
          {label}
        </a>
      );
    }
    return (
      <Link to={`${localePath("/", locale)}#${id}`} onClick={() => setOpen(false)} className={linkClass}>
        {label}
      </Link>
    );
  };

  const getLanguageSwitchPath = (targetLocale: Locale) => {
    // Get the current path without language prefix
    let path = location.pathname;
    // Remove current locale prefix
    for (const loc of SUPPORTED_LOCALES) {
      if (loc !== "no" && path.startsWith(`/${loc}/`)) {
        path = path.slice(loc.length + 1);
        break;
      } else if (loc !== "no" && path === `/${loc}`) {
        path = "/";
        break;
      }
    }
    return localePath(path, targetLocale);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/70 backdrop-blur-md border-b border-border/50">
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
        <Link to={localePath("/", locale)} className="font-display text-xl tracking-[0.15em] text-foreground">
          FUEGOS JL NORDIC
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {scrollLink("produkter", t("nav.grills"))}
          <Link to={localePath("/tilbehor", locale)} onClick={() => setOpen(false)} className={linkClass}>
            {t("nav.accessories")}
          </Link>
          {scrollLink("faq", t("nav.faq"))}
          {scrollLink("bestilling", t("nav.order"))}
          <Link to={localePath("/blogg", locale)} className={linkClass}>
            {t("nav.blog")}
          </Link>

          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-foreground/50 hover:text-foreground transition-colors text-xs tracking-[0.2em] uppercase"
            >
              <Globe size={14} />
              {LOCALE_LABELS[locale]}
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-background border border-border/50 min-w-[80px] z-50">
                {SUPPORTED_LOCALES.map((loc) => (
                  <Link
                    key={loc}
                    to={getLanguageSwitchPath(loc)}
                    onClick={() => setLangOpen(false)}
                    className={`block px-4 py-2 text-xs tracking-[0.2em] transition-colors ${
                      loc === locale ? "text-foreground bg-foreground/5" : "text-foreground/50 hover:text-foreground hover:bg-foreground/5"
                    }`}
                  >
                    {LOCALE_LABELS[loc]}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 md:hidden">
          {/* Mobile language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="text-foreground/50 hover:text-foreground transition-colors text-xs tracking-[0.2em] uppercase flex items-center gap-1"
            >
              <Globe size={14} />
              {LOCALE_LABELS[locale]}
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-background border border-border/50 min-w-[80px] z-50">
                {SUPPORTED_LOCALES.map((loc) => (
                  <Link
                    key={loc}
                    to={getLanguageSwitchPath(loc)}
                    onClick={() => { setLangOpen(false); setOpen(false); }}
                    className={`block px-4 py-2 text-xs tracking-[0.2em] transition-colors ${
                      loc === locale ? "text-foreground bg-foreground/5" : "text-foreground/50 hover:text-foreground hover:bg-foreground/5"
                    }`}
                  >
                    {LOCALE_LABELS[loc]}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setOpen(!open)} className="text-foreground" aria-label={t("nav.menu_label")}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background border-b border-border/50 px-6 pb-6 flex flex-col gap-4">
          {scrollLink("produkter", t("nav.grills"))}
          <Link to={localePath("/tilbehor", locale)} onClick={() => setOpen(false)} className={linkClass}>
            {t("nav.accessories")}
          </Link>
          {scrollLink("faq", t("nav.faq"))}
          {scrollLink("bestilling", t("nav.order"))}
          <Link to={localePath("/blogg", locale)} onClick={() => setOpen(false)} className={linkClass}>
            {t("nav.blog")}
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
